// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 58.3.0 (Phase 25 / Sprint D) — PDPL direct-send types.
 *
 * Captures the end-to-end transmission of the assembled 8-part ZIP +
 * email draft to MoPS A05 via the customer's SMTP provider (Sprint C).
 * Architecture lock (LOCKED — parent epic): AesirX MUST NOT be the
 * default sender. The send-record shape carries `provider_config_id`
 * as a mandatory field — there's no platform-managed fallback path
 * because the calling service refuses to construct a send without a
 * verified customer-controlled provider.
 *
 * Chain-of-custody snapshot fields (`pack_sha256_at_send`,
 * `draft_version_at_send`) freeze the state at send-time. A later
 * pack re-assembly or draft edit cannot rewrite history.
 */
import { COMPLIANCE_EXTERNAL_ACTION_CLASSIFICATIONS, } from './compliance-operations.js';
/** Authority's reply classification. Operator-supplied; reclassification
 *  is allowed (e.g. `acknowledgement` → `acceptance` after the formal
 *  letter arrives). `no_response` is the explicit "we waited but nothing
 *  came back" state operators flag for the audit record. */
/**
 * How an authority's response is classified.
 *
 * **One declaration, two names.** Sprint 65.4.0 needed this vocabulary for
 * `ComplianceExternalAction` and briefly declared a second five-member array
 * beside this one — which is precisely the drift this file's neighbours warn
 * about, and the `shared-vocabulary-census` reported it as four new
 * re-derivations within the hour. The array now lives once, in
 * `compliance-operations.ts`, and this module keeps its own domain name for it.
 *
 * The runtime companion matters as much as the type: before it existed, every
 * consumer needing the values at run time had to transcribe all five, and four
 * of them did.
 */
export const PDPL_AUTHORITY_RESPONSE_CLASSIFICATIONS = COMPLIANCE_EXTERNAL_ACTION_CLASSIFICATIONS;
/** Error codes the service throws via AppError. */
export const PDPL_EMAIL_SEND_ERROR_CODES = {
    PACK_NOT_FOUND: 'PDPL_EMAIL_SEND_PACK_NOT_FOUND',
    PACK_NOT_ASSEMBLED: 'PDPL_EMAIL_SEND_PACK_NOT_ASSEMBLED',
    DRAFT_NOT_FOUND: 'PDPL_EMAIL_SEND_DRAFT_NOT_FOUND',
    DRAFT_PACK_MISMATCH: 'PDPL_EMAIL_SEND_DRAFT_PACK_MISMATCH',
    /** Draft is already in `finalized` status (a prior send succeeded).
     *  Re-sending the same draft would create a duplicate transmission;
     *  the operator should compose a new draft instead. */
    DRAFT_ALREADY_FINALIZED: 'PDPL_EMAIL_SEND_DRAFT_ALREADY_FINALIZED',
    INVALID_RECIPIENTS: 'PDPL_EMAIL_SEND_INVALID_RECIPIENTS',
    SEND_RECORD_NOT_FOUND: 'PDPL_EMAIL_SEND_RECORD_NOT_FOUND',
    INVALID_EVIDENCE_FILE: 'PDPL_EMAIL_SEND_INVALID_EVIDENCE_FILE',
    INVALID_CLASSIFICATION: 'PDPL_EMAIL_SEND_INVALID_CLASSIFICATION',
    ZIP_FETCH_FAILED: 'PDPL_EMAIL_SEND_ZIP_FETCH_FAILED',
    /** `retry_of_send_id` referenced a record that doesn't exist, belongs
     *  to a different org/pack/draft, or whose outcome was not `failed`.
     *  Retrying a successful send would create a duplicate transmission. */
    INVALID_RETRY_LINK: 'PDPL_EMAIL_SEND_INVALID_RETRY_LINK',
};
//# sourceMappingURL=pdpl-email-send.js.map